/**
 * O estado do convite, e nenhum pixel.
 *
 * A separação entre este arquivo e `telas/Convite/` é o que permite renderizar
 * cada estado da tela — carregando, fora da janela, rede caída no meio do
 * preenchimento — sem subir rede nenhuma. Estado que só existe quando o
 * servidor coopera é estado que ninguém revisa antes de o convidado encontrar.
 *
 * Duas regras governam o que está aqui:
 *
 *  - **A tela não decide.** Qual é o próximo nó é resposta de `responder()`.
 *    Não há grafo neste arquivo, nem cópia de condição, nem atalho para "ficar
 *    mais rápido": o roteamento tem uma cópia só, e ela é SQL.
 *  - **O `resposta_token` não sai daqui.** Ele é o acesso àquela resposta e não
 *    aparece no estado que a tela recebe — o que impede que ele acabe num
 *    atributo, num `data-`, ou numa captura de tela de revisão.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { TRACO, emailComMascara } from '../util/formato';

import { servicoDoConvite, type Resultado, type ServicoDoConvite } from '../servicos/convite';
import {
  esquecerRespostaToken,
  lembrarRespostaToken,
  respostaTokenLembrado,
} from '../servicos/sessao-do-convite';
import type {
  EnvelopeDoConvite,
  FimDoConvite,
  Marca,
  MotivoConhecido,
  NoDoConvite,
  RecusaDoConvite,
  SubtipoPergunta,
  TrilhoDoBanco,
  Valor,
} from '../tipos';

/** Uma linha da trilha: o que já foi respondido. Sem total, por decisão. */
export type Trilho = {
  readonly noId: string;
  readonly rotulo: string;
  readonly subtipo: SubtipoPergunta | null;
  readonly valor: Valor;
};

/** O que se repete em toda etapa: quem convida. */
export type Cabecalho = {
  readonly titulo: string;
  readonly descricao: string;
  readonly marca: Marca;
};

export type Problema = {
  readonly mensagem: string;
  /** `true` quando a recusa é sobre o campo — o texto vai colado nele. */
  readonly noCampo: boolean;
};

export type EstadoDoConvite =
  | { readonly situacao: 'carregando' }
  /** Regra de negócio na porta: não publicado, fora da janela, limite, token. */
  | { readonly situacao: 'recusado'; readonly recusa: RecusaDoConvite }
  /** Nem chegamos ao servidor. Diferente de recusa: dá para tentar de novo. */
  | { readonly situacao: 'falhou'; readonly mensagem: string }
  | {
      readonly situacao: 'respondendo';
      readonly cabecalho: Cabecalho;
      readonly no: NoDoConvite;
      readonly trilha: readonly Trilho[];
      readonly enviando: boolean;
      readonly problema: Problema | null;
    }
  | {
      readonly situacao: 'concluido';
      readonly cabecalho: Cabecalho;
      /** O `fim` alcançado. `rotulo` é o título da despedida, não o do evento. */
      readonly no: NoDoConvite | null;
      readonly mensagem: string | null;
      readonly instrucoes: string | null;
      readonly trilha: readonly Trilho[];
      /**
       * `true` quando esta tela veio de uma resposta RETOMADA — o navegador
       * lembrou de um formulário já concluído — e não de alguém que acabou de
       * enviar.
       *
       * A distinção é o produto inteiro deste recurso: quem acabou de responder
       * lê a despedida; quem VOLTOU precisa saber que a resposta já está
       * registrada, e precisa de uma saída caso ela não seja dele.
       */
      readonly retomada: boolean;
      /**
       * O e-mail que esta resposta deu, mascarado (`re***@startse.com`), ou
       * `null` se o formulário não perguntou nenhum.
       *
       * Vem da trilha, que vem do banco a cada abertura — nunca do
       * armazenamento do navegador. Dado pessoal guardado localmente sobrevive
       * ao evento inteiro, e a tela precisa dele por dez segundos.
       */
      readonly emailMascarado: string | null;
    };

export type Convite = {
  readonly estado: EstadoDoConvite;
  /** Responde o nó atual. Em `mensagem`, o valor é `null` — não há o que gravar. */
  readonly enviar: (valor: Valor) => void;
  /** Recomeça a abertura. É o botão da tela de falha de rede. */
  readonly reabrir: () => void;
  /**
   * Descarta a resposta lembrada e abre um formulário limpo. É o "não sou eu"
   * da tela final.
   *
   * Ele não apaga nada no banco: a resposta de quem respondeu antes continua
   * lá, contada e exportável. O que ele apaga é o vínculo ENTRE ESTE NAVEGADOR e
   * aquela resposta — que é exatamente o que sobra errado num celular
   * emprestado.
   */
  readonly naoSouEu: () => void;
};

function cabecalhoDe(envelope: EnvelopeDoConvite): Cabecalho {
  return { titulo: envelope.titulo, descricao: envelope.descricao, marca: envelope.marca };
}

/**
 * Sem próximo nó e sem conclusão: só acontece em rascunho com nó sem saída, que
 * `validar_grafo` recusa na publicação. Existe como estado escrito porque a
 * alternativa é uma tela em branco — e tela em branco o convidado fecha sem
 * conseguir dizer a ninguém o que viu.
 */
const SEM_PROXIMO =
  'Este convite não tem uma próxima etapa. Avise quem enviou o link: o formulário ' +
  'está incompleto.';

/**
 * Os motivos que ENCERRAM o percurso: não há o que o convidado faça, e a tela
 * inteira passa a ser a mensagem do banco.
 *
 * A lista é curta e nomeia só o que de fato fecha a porta. Quem não está nela
 * — motivo novo incluído — mantém a pergunta na tela. É a direção segura do
 * padrão: errar para "continua respondendo" custa uma frase confusa; errar para
 * "acabou" apaga o formulário de quem só marcou a opção errada.
 *
 * `ja_encerrada` não está aqui porque ela não é fim de linha: significa que a
 * resposta já foi enviada, e o lugar de quem está nela é a tela final.
 */
const ENCERRAM: readonly MotivoConhecido[] = [
  'nao_encontrado',
  'nao_publicado',
  'encerrado',
  'fora_da_janela',
  'limite_atingido',
  'ja_respondido',
  'sessao_invalida',
];

export function useConvite(token: string, servico: ServicoDoConvite = servicoDoConvite): Convite {
  const [estado, definirEstado] = useState<EstadoDoConvite>({ situacao: 'carregando' });

  // O que a tela não recebe. Ref, e não estado: mudar qualquer um destes não
  // deve, sozinho, re-renderizar — quem manda na tela é `estado`.
  const respostaToken = useRef<string | null>(null);
  const noAtual = useRef<NoDoConvite | null>(null);
  const trilha = useRef<readonly Trilho[]>([]);
  const cabecalho = useRef<Cabecalho | null>(null);

  // A aplicação monta em `StrictMode`, que roda o efeito DUAS vezes em
  // desenvolvimento. `abrir_formulario` com link aberto CRIA uma resposta a
  // cada chamada — sem esta trava, todo formulário público nasceria com o dobro
  // de respostas abandonadas, e só em produção alguém notaria que não era isso.
  const abertoPara = useRef<string | null>(null);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  const publicar = useCallback((novo: EstadoDoConvite) => {
    if (vivo.current) definirEstado(novo);
  }, []);

  /**
   * A tela final. `concluir` é a porta documentada — é ela que fecha e dispara
   * as ações do caminho — e é idempotente, então recarregar a página final
   * repete a chamada sem repetir efeito.
   */
  const finalizar = useCallback(
    async (deReserva: NoDoConvite | null, retomada = false) => {
      const guardado = respostaToken.current;
      const resultado: Resultado<FimDoConvite> | null = guardado
        ? await servico.concluir(guardado)
        : null;

      if (resultado?.estado === 'ok') {
        const fim = resultado.envelope;
        cabecalho.current = cabecalhoDe(fim);
        noAtual.current = fim.no;
        publicar({
          situacao: 'concluido',
          cabecalho: cabecalho.current,
          no: fim.no,
          mensagem: fim.mensagem,
          instrucoes: fim.instrucoes,
          trilha: trilha.current,
          retomada,
          emailMascarado: emailDaTrilha(trilha.current),
        });
        return;
      }

      if (resultado?.estado === 'recusa' && resultado.recusa.motivo !== 'incompleta') {
        publicar({ situacao: 'recusado', recusa: resultado.recusa });
        return;
      }

      // A rede caiu na última chamada, mas mensagem e instruções JÁ vieram
      // dentro do `fim` que o `responder` devolveu — `no_para_convidado` libera
      // as duas para o tipo `fim`. Mostrar o que está na mão não é inventar
      // dado: é a mesma fonte, na mesma resposta. Esconder as instruções de
      // chegada por causa de uma requisição que falhou é que seria perda.
      const reserva = deReserva ?? noAtual.current;
      const cabeca = cabecalho.current;
      if (reserva && cabeca) {
        publicar({
          situacao: 'concluido',
          cabecalho: cabeca,
          no: reserva,
          mensagem: textoDaConfig(reserva, 'mensagem'),
          instrucoes: textoDaConfig(reserva, 'instrucoes'),
          trilha: trilha.current,
          retomada,
          emailMascarado: emailDaTrilha(trilha.current),
        });
        return;
      }

      publicar({
        situacao: 'falhou',
        mensagem: resultado?.estado === 'falha' ? resultado.mensagem : SEM_PROXIMO,
      });
    },
    [publicar, servico],
  );

  const aplicar = useCallback(
    async (envelope: EnvelopeDoConvite, retomada = false) => {
      respostaToken.current = envelope.resposta_token;
      cabecalho.current = cabecalhoDe(envelope);
      lembrarRespostaToken(token, envelope.resposta_token);

      if (envelope.concluida) {
        await finalizar(envelope.no, retomada);
        return;
      }

      if (!envelope.no) {
        publicar({ situacao: 'falhou', mensagem: SEM_PROXIMO });
        return;
      }

      noAtual.current = envelope.no;
      publicar({
        situacao: 'respondendo',
        cabecalho: cabecalho.current,
        no: envelope.no,
        trilha: trilha.current,
        enviando: false,
        problema: null,
      });
    },
    [finalizar, publicar, token],
  );

  const abrir = useCallback(async () => {
    publicar({ situacao: 'carregando' });

    // ── A retomada, e o defeito que ela conserta ────────────────────────────
    //
    // `sessionStorage` guarda o token daquela resposta. A primeira versão disto
    // chamava `concluir` — e `concluir` só devolve envelope quando a resposta
    // CHEGOU AO FIM. No meio do caminho ela recusa com `incompleta`, e o código
    // então descartava o token e caía em `abrir`, que num link aberto CRIA UMA
    // RESPOSTA NOVA.
    //
    // O efeito para quem respondia: quatro de seis perguntas dadas, girou a
    // tela, formulário em branco. As quatro respostas continuavam no banco,
    // órfãs. É o pior defeito deste marco, porque o que ele perde é trabalho de
    // gente — e de gente que não vai reclamar com ninguém.
    //
    // `retomar` (0010) é a chamada que faltava: devolve a resposta em andamento
    // com o nó ATUAL e a trilha do que já foi dado, e serve depois do fim
    // também — aí `concluida` volta `true` e `aplicar` encaminha para a tela
    // final, que é o caminho que já existia.
    const lembrado = respostaTokenLembrado(token);
    if (lembrado) {
      const retomada = await servico.retomar(lembrado);

      if (retomada.estado === 'ok') {
        trilha.current = retomada.envelope.trilha.map(daTrilhaDoBanco);
        // O `true` é o recurso inteiro: daqui em diante a tela sabe distinguir
        // "acabou de enviar" de "voltou e já estava enviado".
        await aplicar(retomada.envelope, true);
        return;
      }

      // Rede caída na retomada NÃO pode virar formulário novo.
      //
      // Cair em `abrir` aqui reintroduziria o defeito inteiro por outra porta —
      // um blip de 3G no meio do preenchimento apagaria tudo, e essa é a rede
      // de quem confirma presença no ônibus. O token continua guardado, o botão
      // de "Tentar de novo" volta a esta função, e a resposta continua lá.
      if (retomada.estado === 'falha') {
        publicar({ situacao: 'falhou', mensagem: retomada.mensagem });
        return;
      }

      // Recusa é diferente: o banco olhou o token e disse que ele não abre nada
      // (`sessao_invalida`). Guardar segredo que não serve para nada é só
      // superfície — some com ele e abre do zero, que é o certo.
      esquecerRespostaToken(token);
      respostaToken.current = null;
    }

    const resultado = await servico.abrir(token);
    if (resultado.estado === 'falha') {
      publicar({ situacao: 'falhou', mensagem: resultado.mensagem });
      return;
    }
    if (resultado.estado === 'recusa') {
      publicar({ situacao: 'recusado', recusa: resultado.recusa });
      return;
    }
    await aplicar(resultado.envelope);
  }, [aplicar, publicar, servico, token]);

  useEffect(() => {
    if (abertoPara.current === token) return;
    abertoPara.current = token;
    trilha.current = [];
    void abrir();
  }, [abrir, token]);

  const enviar = useCallback(
    (valor: Valor) => {
      const no = noAtual.current;
      const guardado = respostaToken.current;
      const cabeca = cabecalho.current;
      if (!no || !guardado || !cabeca) return;

      publicar({
        situacao: 'respondendo',
        cabecalho: cabeca,
        no,
        trilha: trilha.current,
        enviando: true,
        problema: null,
      });

      void (async () => {
        const resultado = await servico.responder(guardado, no.id, valor);

        if (resultado.estado === 'ok') {
          // A trilha guarda o que o convidado respondeu, não por onde o motor
          // passou: nó de ação e de condição não param nele, e listá-los
          // contaria um percurso que ninguém viu.
          if (no.tipo === 'pergunta') {
            trilha.current = [
              ...trilha.current,
              { noId: no.id, rotulo: no.rotulo, subtipo: no.subtipo, valor },
            ];
          }
          await aplicar(resultado.envelope);
          return;
        }

        if (resultado.estado === 'falha') {
          publicar({
            situacao: 'respondendo',
            cabecalho: cabeca,
            no,
            trilha: trilha.current,
            enviando: false,
            problema: { mensagem: resultado.mensagem, noCampo: false },
          });
          return;
        }

        const recusa = resultado.recusa;

        // Duplo clique no último botão: a resposta já fechou, e o `fim` veio
        // junto. Levar à tela final é o certo — o convidado respondeu tudo.
        if (recusa.motivo === 'ja_encerrada') {
          await finalizar(recusa.no ?? null);
          return;
        }

        // Motivo que ENCERRA vira tela inteira; todo o resto — `obrigatorio`,
        // `valor_invalido`, `fora_de_ordem`, e o motivo que o banco ainda vai
        // inventar — mantém a pergunta na tela com a mensagem colada nela.
        //
        // O padrão é este, e não o contrário, porque a união de motivos é
        // aberta: um `valor_invalido` caindo no ramo de encerrar apagaria o
        // formulário na cara de quem só marcou a opção errada. Ficar na
        // pergunta com a frase do banco é seguro para qualquer motivo novo.
        if (ENCERRAM.some((motivo) => motivo === recusa.motivo)) {
          publicar({ situacao: 'recusado', recusa });
          return;
        }

        // O nó que a recusa devolve diz de que ela fala: veio o MESMO nó, e o
        // problema é a resposta que acabou de ser dada (a mensagem vai colada
        // no campo); veio OUTRO, e o convidado estava numa aba velha (a
        // mensagem vira aviso, e a pergunta de verdade toma a tela). Ler isso
        // do nó, e não de uma lista de motivos, é o que faz valer para o motivo
        // que ainda não existe.
        const proximo = recusa.no ?? no;
        noAtual.current = proximo;
        publicar({
          situacao: 'respondendo',
          cabecalho: cabeca,
          no: proximo,
          trilha: trilha.current,
          enviando: false,
          problema: { mensagem: recusa.mensagem, noCampo: proximo.id === no.id },
        });
      })();
    },
    [aplicar, finalizar, publicar, servico],
  );

  const reabrir = useCallback(() => {
    abertoPara.current = token;
    trilha.current = [];
    void abrir();
  }, [abrir, token]);

  /**
   * "Não sou eu" — descarta a resposta lembrada e abre um formulário limpo.
   *
   * ── O que ele NÃO faz ────────────────────────────────────────────────────
   *
   * Não apaga nada no banco. A resposta de quem respondeu antes continua lá,
   * contada no painel e presente na exportação — apagá-la seria deixar alguém
   * que pegou um celular emprestado destruir a confirmação de outra pessoa com
   * um clique, e o percurso de uma resposta é registro de auditoria.
   *
   * O que ele apaga é o VÍNCULO entre este navegador e aquela resposta. Depois
   * dele, `abrir()` não acha token lembrado e `abrir_formulario` cria uma
   * resposta nova — que é o que quem está com o aparelho na mão queria.
   *
   * ── Por que ele existe ───────────────────────────────────────────────────
   *
   * Ele é a metade que torna o `localStorage` defensável. Guardar o token além
   * da aba evita a resposta duplicada de quem volta pelo mesmo link; o preço é o
   * dispositivo compartilhado, e este botão é o troco. Removê-lo reabre o
   * problema sem nada no lugar — está escrito assim em
   * `servicos/sessao-do-convite.ts`, e o guard de `testes/disciplina.test.ts`
   * aponta para lá.
   */
  const naoSouEu = useCallback(() => {
    esquecerRespostaToken(token);
    respostaToken.current = null;
    noAtual.current = null;
    trilha.current = [];
    // `abertoPara` volta para o token: `abrir()` é chamado à mão logo abaixo, e
    // sem isso o efeito de montagem dispararia uma SEGUNDA abertura — duas
    // respostas em branco para um clique.
    abertoPara.current = token;
    void abrir();
  }, [abrir, token]);

  return { estado, enviar, reabrir, naoSouEu };
}

/**
 * O e-mail que esta resposta deu, mascarado — ou `null`.
 *
 * Sai da trilha, que vem do banco. Procura o ÚLTIMO item de subtipo `email`: um
 * formulário pode perguntar mais de um (o do convidado e o de quem indicou), e o
 * último é o que a pessoa acabou de escrever sobre si.
 *
 * `null` quando o formulário não pergunta e-mail nenhum — e aí a tela diz que a
 * resposta já foi registrada sem dizer de quem, que é tudo o que ela pode
 * honestamente afirmar.
 */
function emailDaTrilha(trilha: readonly Trilho[]): string | null {
  for (let i = trilha.length - 1; i >= 0; i -= 1) {
    const passo = trilha[i];
    if (passo?.subtipo !== 'email') continue;
    const bruto = typeof passo.valor === 'string' ? passo.valor : '';
    const mascarado = emailComMascara(bruto);
    return mascarado === TRACO ? null : mascarado;
  }
  return null;
}

/**
 * A trilha do banco vira a trilha da tela.
 *
 * A conversão existe num lugar só para que `no_id` — nome de coluna — não
 * atravesse para dentro dos componentes. É a mesma fronteira que faz `api.ts`
 * ser o único arquivo que conhece o Supabase.
 */
function daTrilhaDoBanco(linha: TrilhoDoBanco): Trilho {
  return {
    noId: linha.no_id,
    rotulo: linha.rotulo,
    subtipo: linha.subtipo,
    valor: linha.valor,
  };
}

/** `config` é `Record<string, unknown>`: o que não for texto não vira texto. */
function textoDaConfig(no: NoDoConvite, chave: string): string | null {
  const valor = no.config[chave];
  return typeof valor === 'string' && valor.trim() !== '' ? valor : null;
}
